<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';
import { useConfig } from 'dashboard/composables/useConfig';
import { useVisionAccess } from 'dashboard/composables/useVisionAccess';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { toast } from 'dashboard/helper/toast';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConversationAiSummaryAPI from 'dashboard/api/conversationAiSummary';
import {
  visionPromptsFor,
  buildVisionPrompt,
} from 'dashboard/components-next/vision/quickPrompts';

// Atalhos de IA do reply box — só em inbox vinculada a projeto do Studio
// (quem renderiza já gateia por `inbox.ai_project_id`). Dois grupos:
//   · Vision: abre o painel já perguntando (BUS_EVENTS.VISION_ASK); exige o
//     CPF/CNPJ do contato e a flag premium `vision`.
//   · Resumo: POST → job Rails → nota privada na conversa. Falha do job também
//     vira nota (fail loud); aqui só o 4xx/5xx do POST vira toast.
const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const { isEnterprise } = useConfig();

const currentChat = useMapGetter('getSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const getAccount = useMapGetter('accounts/getAccount');
// Vision é do administrador; agente só tem com tools liberadas no papel.
const { visionAllowed, allowedTools } = useVisionAccess();

const isOpen = ref(false);
const isSummarizing = ref(false);

const contactDocument = computed(() => {
  const attributes = currentChat.value?.meta?.sender?.custom_attributes || {};
  return attributes.cpf || attributes.cnpj || '';
});
const selectedContract = computed(
  () => currentChat.value?.additional_attributes?.ai_selected_contract || null
);

const showVision = computed(
  () =>
    isEnterprise &&
    visionAllowed.value &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.VISION
    )
);

// Resumo: Studio (feature `ai`) ligado E credencial OpenAI cadastrada no Studio
// (`ai_openai_credential_present` do payload da conta — booleano, nunca a chave).
// O backend repete os dois gates; aqui é pra o item nem aparecer.
const showSummary = computed(
  () =>
    isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.AI) &&
    !!getAccount.value(currentAccountId.value)?.ai_openai_credential_present
);

const menuItems = computed(() => {
  const items = [];
  if (showVision.value) {
    // Só os atalhos cujas tools o papel libera — administrador vê todos.
    // Oferecer um atalho sem a tool por trás é prometer um 403.
    visionPromptsFor(allowedTools.value).forEach(option => {
      items.push({
        action: 'vision',
        value: option.key,
        icon: option.icon,
        label: t(option.label),
        tooltip: t('CONVERSATION.FOOTER.AI_ACTIONS.VISION_TOOLTIP'),
        option,
      });
    });
  }
  if (!showSummary.value) return items;
  items.push(
    {
      action: 'summary',
      value: 'current',
      icon: 'i-lucide-text-quote',
      label: t('CONVERSATION.FOOTER.AI_ACTIONS.SUMMARIZE'),
      tooltip: t('CONVERSATION.FOOTER.AI_ACTIONS.SUMMARIZE_TOOLTIP'),
      disabled: isSummarizing.value,
    },
    {
      action: 'summary',
      value: 'with_previous',
      icon: 'i-lucide-history',
      label: t('CONVERSATION.FOOTER.AI_ACTIONS.SUMMARIZE_WITH_PREVIOUS'),
      tooltip: t(
        'CONVERSATION.FOOTER.AI_ACTIONS.SUMMARIZE_WITH_PREVIOUS_TOOLTIP'
      ),
      disabled: isSummarizing.value,
    }
  );
  return items;
});

const askVision = option => {
  if (!contactDocument.value) {
    // Sem documento o agente não tem o que consultar: avisa em vez de mandar
    // a pergunta cega (o VisionEmptyState também esconde as chips sem CPF).
    toast.error(t('CONVERSATION.FOOTER.AI_ACTIONS.NO_DOCUMENT'));
    return;
  }
  emitter.emit(BUS_EVENTS.VISION_ASK, {
    prompt: buildVisionPrompt(
      t,
      option,
      contactDocument.value,
      selectedContract.value
    ),
  });
};

const summarize = async includePrevious => {
  isSummarizing.value = true;
  try {
    await toast.promise(
      ConversationAiSummaryAPI.create({
        conversationId: props.conversationId,
        includePrevious,
      }),
      {
        loading: t('CONVERSATION.FOOTER.AI_ACTIONS.SUMMARY_LOADING'),
        success: t('CONVERSATION.FOOTER.AI_ACTIONS.SUMMARY_QUEUED'),
        error: error =>
          error?.response?.data?.error ||
          t('CONVERSATION.FOOTER.AI_ACTIONS.SUMMARY_ERROR'),
      }
    );
  } finally {
    isSummarizing.value = false;
  }
};

const handleAction = ({ action, value, option }) => {
  isOpen.value = false;
  if (action === 'vision') askVision(option);
  if (action === 'summary') summarize(value === 'with_previous');
};
</script>

<template>
  <div
    v-if="menuItems.length"
    v-on-click-outside="() => (isOpen = false)"
    class="relative"
  >
    <Button
      v-tooltip.top-end="$t('CONVERSATION.FOOTER.AI_ACTIONS.TOOLTIP')"
      icon="i-lucide-sparkles"
      slate
      faded
      sm
      :is-loading="isSummarizing"
      @click="isOpen = !isOpen"
    />
    <DropdownMenu
      v-if="isOpen"
      :menu-items="menuItems"
      class="bottom-full mb-2 ltr:left-0 rtl:right-0 w-72"
      @action="handleAction"
    />
  </div>
</template>
